import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BlogPost, categoryColors, fetchBlogs } from "@/pages/blog/api";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { BLOG_INDEX_SEO } from "@/constants/seoConfig";

const categories = ["All", "Mental Health", "AI & Technology", "Emotional Wellness", "Relationships", "Self-Care"];

export default function BlogPage() {
  const blogSeo = useMemo(() => BLOG_INDEX_SEO, []);
  useSEO(blogSeo);

  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadBlogs = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchBlogs({
          page: 1,
          limit: 100,
          category: activeCategory === "All" ? undefined : activeCategory,
          search: searchQuery.trim() || undefined,
        });
        setPosts(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unable to load blog articles right now.");
      } finally {
        setLoading(false);
      }
    };

    const timeout = setTimeout(loadBlogs, 300);
    return () => clearTimeout(timeout);
  }, [activeCategory, searchQuery]);

  const filtered = useMemo(() => posts, [posts]);

  const featured = filtered.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured || activeCategory !== "All" || searchQuery);

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <section
          className="pt-28 pb-14 px-4 md:px-8 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #f8f5ff 100%)" }}
          aria-label="Blog hero section"
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
            <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-0 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <nav aria-label="Breadcrumb" className="flex justify-center mb-4">
              <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/" className="hover:text-[#7C3AED] transition-colors" itemProp="item"><span itemProp="name">Home</span></Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span className="text-[#7C3AED] font-medium" itemProp="name">Blog</span>
                  <meta itemProp="position" content="2" />
                </li>
              </ol>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
              <i className="ri-article-line"></i>
              Insights &amp; Stories
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-4 leading-tight">
              The Ava Blog
            </h1>
            <p className="text-[#5a4a8a] text-base max-w-xl mx-auto mb-8">
              Thoughts on emotional wellness, AI companionship, and what it means to feel truly heard.
            </p>
            <div className="relative max-w-md mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-[#7a6aaa] text-sm"></i>
              <input
                type="search"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full border border-[#e0d8ff] bg-white text-sm text-[#1a1040] placeholder-[#aaa] outline-none focus:border-[#7C3AED] transition-colors"
              />
            </div>
          </div>
        </section>

        {/* Blend: gradient to white */}
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <section className="sticky top-16 z-30 bg-white/90 backdrop-blur-md border-b border-[#e8e0ff] px-4 md:px-8 py-3" aria-label="Category filter">
          <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all ${
                  activeCategory === cat
                    ? "text-white"
                    : "text-[#7a6aaa] bg-[#f5f0ff] hover:bg-[#ede8ff]"
                }`}
                style={activeCategory === cat ? { background: "linear-gradient(135deg, #7C3AED, #A855F7)" } : {}}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
          {featured && activeCategory === "All" && !searchQuery && (
            <article
              className="mb-14 rounded-3xl overflow-hidden border border-[#e8e0ff] bg-white group cursor-pointer"
              itemScope
              itemType="https://schema.org/BlogPosting"
            >
              <meta itemProp="author" content="Ava Team" />
              <meta itemProp="publisher" content="AvachatAI" />
              <Link to={`/blog/${featured.slug}`} className="flex flex-col lg:flex-row">
                <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    title={`${featured.title} - AvachatAI Blog`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    itemProp="image"
                  />
                </div>
                <div className="lg:w-1/2 p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold text-white"
                      style={{ background: categoryColors[featured.category] || "#7C3AED" }}
                    >
                      {featured.category}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#7C3AED" }}>
                      Featured
                    </span>
                  </div>
                  <h2 className="font-display text-xl md:text-2xl font-bold text-[#1a1040] mb-3 leading-snug group-hover:text-[#7C3AED] transition-colors" itemProp="headline">
                    {featured.title}
                  </h2>
                  <p className="text-[#7a6aaa] text-sm leading-relaxed mb-5" itemProp="description">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-[#9a8aaa]">
                    <span itemProp="author">{featured.author}</span>
                    <span>·</span>
                    <time itemProp="datePublished">{featured.date}</time>
                    <span>·</span>
                    <span>{featured.readTime}</span>
                  </div>
                </div>
              </Link>
            </article>
          )}

          {loading ? (
            <div className="text-center py-20">
              <i className="ri-loader-4-line text-4xl text-[#7C3AED] mb-4 block animate-spin"></i>
              <p className="text-[#7a6aaa] text-base">Loading articles...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
              <p className="text-[#7a6aaa] text-base mb-4">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-5 py-2 rounded-full text-white text-sm font-semibold"
                style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
              >
                Try again
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-search-line text-4xl text-[#c4b8f0] mb-4 block"></i>
              <p className="text-[#7a6aaa] text-base">No articles found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === "All" && !searchQuery ? rest : filtered).map((post) => (
                <article
                  key={post.slug}
                  className="rounded-2xl overflow-hidden border border-[#e8e0ff] bg-white group cursor-pointer hover:-translate-y-1 transition-transform duration-300"
                  itemScope
                  itemType="https://schema.org/BlogPosting"
                >
                  <meta itemProp="author" content="Ava Team" />
                  <Link to={`/blog/${post.slug}`}>
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        title={`${post.title} - AvachatAI Blog`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        itemProp="image"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                          style={{ background: categoryColors[post.category] || "#7C3AED" }}
                        >
                          {post.category}
                        </span>
                        <span className="text-xs text-[#9a8aaa]">{post.readTime}</span>
                      </div>
                      <h3 className="font-display font-bold text-[#1a1040] text-base mb-2 leading-snug group-hover:text-[#7C3AED] transition-colors line-clamp-2" itemProp="headline">
                        {post.title}
                      </h3>
                      <p className="text-[#7a6aaa] text-xs leading-relaxed mb-4 line-clamp-3" itemProp="description">{post.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-[#9a8aaa]">
                          <span itemProp="author">{post.author}</span>
                          <span>·</span>
                          <time itemProp="datePublished">{post.date}</time>
                        </div>
                        <i className="ri-arrow-right-line text-[#7C3AED] text-sm group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Blend: white to gradient */}
      <div className="h-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
      <Footer />
    </>
  );
}